import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../services/api'

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled'
export type ProjectMemberRole = 'manager' | 'member' | 'viewer'
export type TaskStatus = 'todo' | 'in_progress' | 'review' | 'done' | 'blocked'
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent'
export type DependencyType = 'finish_to_start' | 'start_to_start' | 'finish_to_finish' | 'start_to_finish'

export const TASK_STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'review', 'done', 'blocked']
export const PROJECT_STATUSES: ProjectStatus[] = ['planning', 'active', 'on_hold', 'completed', 'cancelled']
export const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent']

export interface Project {
  id: number
  company_id: number
  name: string
  description?: string | null
  status: ProjectStatus
  start_date?: string | null
  end_date?: string | null
  owner_id?: number | null
  owner_name?: string | null
  color?: string
  is_active: boolean
  created_by?: number | null
  created_at?: string
  updated_at?: string
  task_count?: number
  member_count?: number
  members?: ProjectMember[]
}

export interface ProjectMember {
  id: number
  project_id: number
  user_id: number
  user_name?: string | null
  user_email?: string | null
  role_on_project: ProjectMemberRole
  joined_at?: string
}

export interface Task {
  id: number
  project_id: number
  company_id: number
  title: string
  description?: string | null
  status: TaskStatus
  priority: TaskPriority
  assignee_id?: number | null
  assignee_name?: string | null
  start_date?: string | null
  due_date?: string | null
  completion_percent: number
  position: number
  created_by?: number | null
  is_active: boolean
  created_at?: string
  updated_at?: string
  dependencies?: TaskDependency[]
}

export interface TaskDependency {
  id: number
  task_id: number
  depends_on_task_id: number
  type: DependencyType
  created_at?: string
}

export interface TaskComment {
  id: number
  task_id: number
  user_id: number
  user_name?: string | null
  content: string
  created_at: string
}

export interface BoardData {
  project: Project
  columns: Record<TaskStatus, Task[]>
  statuses: TaskStatus[]
}

export interface GanttData {
  project: Project
  tasks: Task[]
  dependencies: TaskDependency[]
}

interface ProjectStore {
  projects: Project[]
  currentProjectId: number | null
  currentProject: Project | null
  board: BoardData | null
  gantt: GanttData | null
  isLoading: boolean
  error: string | null

  setCurrentProjectId: (id: number | null) => void

  fetchProjects: (params?: Record<string, string | number | boolean>) => Promise<void>
  fetchProject: (id: number) => Promise<Project | null>
  createProject: (data: Partial<Project>) => Promise<Project>
  updateProject: (id: number, data: Partial<Project>) => Promise<Project>
  deleteProject: (id: number) => Promise<void>

  fetchBoard: (projectId: number) => Promise<BoardData | null>
  fetchGantt: (projectId: number) => Promise<GanttData | null>

  createTask: (projectId: number, data: Partial<Task>) => Promise<Task>
  updateTask: (taskId: number, data: Partial<Task>) => Promise<Task>
  moveTask: (taskId: number, status: TaskStatus, position: number) => Promise<void>
  deleteTask: (taskId: number) => Promise<void>

  addMember: (projectId: number, userId: number, role: ProjectMemberRole) => Promise<void>
  removeMember: (projectId: number, userId: number) => Promise<void>

  fetchComments: (taskId: number) => Promise<TaskComment[]>
  addComment: (taskId: number, content: string) => Promise<TaskComment>

  addDependency: (taskId: number, dependsOnTaskId: number, type?: DependencyType) => Promise<TaskDependency>
  removeDependency: (taskId: number, depId: number) => Promise<void>
}

export const useProjectStore = create<ProjectStore>()(
  persist(
    (set, get) => ({
      projects: [],
      currentProjectId: null,
      currentProject: null,
      board: null,
      gantt: null,
      isLoading: false,
      error: null,

      setCurrentProjectId: (id) => set({ currentProjectId: id }),

      fetchProjects: async (params = {}) => {
        set({ isLoading: true, error: null })
        try {
          const data = await apiGet<{ items: Project[] }>('/projects', params)
          set({ projects: data.items || [], isLoading: false })
        } catch (e) {
          set({ error: 'Failed to load projects', isLoading: false })
        }
      },

      fetchProject: async (id) => {
        set({ isLoading: true, error: null })
        try {
          const data = await apiGet<Project>(`/projects/${id}`)
          set({ currentProject: data, currentProjectId: id, isLoading: false })
          return data
        } catch (e) {
          set({ error: 'Failed to load project', isLoading: false })
          return null
        }
      },

      createProject: async (data) => {
        const project = await apiPost<Project>('/projects', data)
        set({ projects: [project, ...get().projects] })
        return project
      },

      updateProject: async (id, data) => {
        const project = await apiPut<Project>(`/projects/${id}`, data)
        set({
          projects: get().projects.map((p) => (p.id === id ? project : p)),
          currentProject: get().currentProject?.id === id ? project : get().currentProject,
        })
        return project
      },

      deleteProject: async (id) => {
        await apiDelete(`/projects/${id}`)
        set({
          projects: get().projects.filter((p) => p.id !== id),
          currentProject: get().currentProject?.id === id ? null : get().currentProject,
        })
      },

      fetchBoard: async (projectId) => {
        try {
          const data = await apiGet<BoardData>(`/projects/${projectId}/board`)
          set({ board: data })
          return data
        } catch (e) {
          set({ error: 'Failed to load board' })
          return null
        }
      },

      fetchGantt: async (projectId) => {
        try {
          const data = await apiGet<GanttData>(`/projects/${projectId}/gantt`)
          set({ gantt: data })
          return data
        } catch (e) {
          set({ error: 'Failed to load gantt' })
          return null
        }
      },

      createTask: async (projectId, data) => {
        const task = await apiPost<Task>(`/projects/${projectId}/tasks`, data)
        // Optimistically add to board if loaded
        const board = get().board
        if (board && board.project.id === projectId) {
          const next = { ...board, columns: { ...board.columns } }
          next.columns[task.status] = [...(next.columns[task.status] || []), task]
          set({ board: next })
        }
        return task
      },

      updateTask: async (taskId, data) => {
        const task = await apiPut<Task>(`/projects/tasks/${taskId}`, data)
        // Refresh in board
        const board = get().board
        if (board) {
          const next = { ...board, columns: { ...board.columns } }
          for (const status of Object.keys(next.columns) as TaskStatus[]) {
            next.columns[status] = next.columns[status].map((t) => (t.id === taskId ? task : t))
          }
          set({ board: next })
        }
        return task
      },

      moveTask: async (taskId, status, position) => {
        // Optimistic update: rearrange board state immediately
        const board = get().board
        let snapshot: BoardData | null = null
        if (board) {
          snapshot = { ...board, columns: Object.fromEntries(
            Object.entries(board.columns).map(([k, v]) => [k, [...v]])
          ) as Record<TaskStatus, Task[]> }
          let moving: Task | undefined
          const next = { ...board, columns: { ...board.columns } }
          for (const s of Object.keys(next.columns) as TaskStatus[]) {
            const idx = next.columns[s].findIndex((t) => t.id === taskId)
            if (idx >= 0) {
              moving = next.columns[s][idx]
              next.columns[s] = [...next.columns[s].slice(0, idx), ...next.columns[s].slice(idx + 1)]
              break
            }
          }
          if (moving) {
            const updated = { ...moving, status, position }
            const target = [...(next.columns[status] || [])]
            const insertAt = target.findIndex((t) => t.position > position)
            if (insertAt === -1) target.push(updated)
            else target.splice(insertAt, 0, updated)
            next.columns[status] = target
            set({ board: next })
          }
        }
        try {
          await apiPatch(`/projects/tasks/${taskId}/move`, { status, position })
        } catch (e) {
          // Rollback
          if (snapshot) set({ board: snapshot })
          throw e
        }
      },

      deleteTask: async (taskId) => {
        await apiDelete(`/projects/tasks/${taskId}`)
        const board = get().board
        if (board) {
          const next = { ...board, columns: { ...board.columns } }
          for (const status of Object.keys(next.columns) as TaskStatus[]) {
            next.columns[status] = next.columns[status].filter((t) => t.id !== taskId)
          }
          set({ board: next })
        }
      },

      addMember: async (projectId, userId, role) => {
        await apiPost(`/projects/${projectId}/members`, { user_id: userId, role_on_project: role })
        await get().fetchProject(projectId)
      },

      removeMember: async (projectId, userId) => {
        await apiDelete(`/projects/${projectId}/members/${userId}`)
        await get().fetchProject(projectId)
      },

      fetchComments: async (taskId) => {
        const data = await apiGet<{ items: TaskComment[] }>(`/projects/tasks/${taskId}/comments`)
        return data.items || []
      },

      addComment: async (taskId, content) => {
        return await apiPost<TaskComment>(`/projects/tasks/${taskId}/comments`, { content })
      },

      addDependency: async (taskId, dependsOnTaskId, type = 'finish_to_start') => {
        return await apiPost<TaskDependency>(`/projects/tasks/${taskId}/dependencies`, {
          depends_on_task_id: dependsOnTaskId,
          type,
        })
      },

      removeDependency: async (taskId, depId) => {
        await apiDelete(`/projects/tasks/${taskId}/dependencies/${depId}`)
      },
    }),
    {
      name: 'project-storage',
      partialize: (state) => ({ currentProjectId: state.currentProjectId }),
    }
  )
)
